// The account's actions a person confirms with a passkey, unless their
// session is ten minutes old at most (people/mod.rs on the server), or they
// turned confirmations off (people/confirmations.rs). As for a
// runner's code (runners/registration.ts), the confirmation names its person
// itself: adding a passkey binds nothing more, and its digest is the SHA-256
// of the empty string; adding an address binds the address, lower-cased;
// removing a passkey binds its id, as the server writes it.
import type { ConfirmAction, Me } from '../api/types'

/**
 * Whether `me` confirms their actions with a passkey: yes unless the server
 * says their account asks for none (Poulpe-686). An older server, which does
 * not say, always asks.
 */
export function confirmsActions(me: Me | null | undefined): boolean {
  return me?.confirms_actions !== false
}

/** What a person confirms to add a passkey: `add_passkey`, and the SHA-256 of the empty string. */
export const ADD_PASSKEY: ConfirmAction = {
  kind: 'add_passkey',
  digest: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
}

/**
 * What a person confirms to stop confirming their actions with a passkey
 * (Poulpe-686): `stop_confirmations`, and the SHA-256 of the empty string.
 * Turning confirmations back on takes none.
 */
export const STOP_CONFIRMATIONS: ConfirmAction = {
  kind: 'stop_confirmations',
  digest: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
}

/** The SHA-256 of `text`'s UTF-8 bytes, in lowercase hex. */
async function sha256Hex(text: string): Promise<string> {
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)))
  return [...digest].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** What a person confirms to add `address`: `add_email`, and the SHA-256 of its lower-cased bytes, in hex. */
export async function addEmailAction(address: string): Promise<ConfirmAction> {
  return { kind: 'add_email', digest: await sha256Hex(address.toLowerCase()) }
}

/**
 * What a person confirms to remove a passkey: `remove_passkey`, and the
 * SHA-256 of its id's canonical string (lowercase, hyphenated, as the list
 * gives it), in hex. Always asked for, fresh session or not, unless the
 * person turned confirmations off.
 */
export async function removePasskeyAction(id: string): Promise<ConfirmAction> {
  return { kind: 'remove_passkey', digest: await sha256Hex(id) }
}
